import React, { useState, useRef } from 'react';
import {X, Calculator, RefreshCw, Download, Waves, Zap, AlertCircle} from 'lucide-react';
import html2canvas from 'html2canvas';

interface Props {
  onClose: () => void;
}

interface Results {
  CMenor: string;
  CMayor: string;
  FMenor: string;
  FMayor: string;
  MMenor: string;
  MMayor: string;
  RMenor: string;
  RMayor: string;
  tirc: string;
  LSaltoP: string;
  LSaltoS: string;
  LSaltoSi: string;
  penergia: string;
  asalto: string;
  Esalto: string;
  tsalto: string;
}

const emptyResults: Results = {
  CMenor: '', CMayor: '', FMenor: '', FMayor: '',
  MMenor: '', MMayor: '', RMenor: '', RMayor: '',
  tirc: '', LSaltoP: '', LSaltoS: '', LSaltoSi: '',
  penergia: '', asalto: '', Esalto: '', tsalto: ''
};

const SaltoHidraulicoCalculator: React.FC<Props> = ({ onClose }) => {
  const [gasto, setGasto] = useState('');
  const [ancho, setAncho] = useState('');
  const [talud, setTalud] = useState('');
  const [tirConjugado, setTirConjugado] = useState('');
  const [results, setResults] = useState<Results>(emptyResults);
  const [error, setError] = useState('');
  const exportRef = useRef<HTMLDivElement>(null);

  const trunc = (v: number) => Math.trunc(v * 10000) / 10000;

  const calcular = () => {
    setError('');
    const q = Number(gasto);
    const b = Number(ancho);
    const ks = Number(talud);
    const tir = Number(tirConjugado);

    if (q <= 0 || tir <= 0 || b < 0 || ks < 0) {
      setError('Faltan datos o son irreales');
      setResults(emptyResults);
      return;
    }
    if (b === 0 && ks === 0) {
      setError('No existe una sección con esas características');
      setResults(emptyResults);
      return;
    }

    // Centroide y momento M1 (sección 1)
    const computeCentroidArea = (depth: number) => {
      const x1 = 0, y1 = 0;
      const x2 = b, y2 = 0;
      const x3 = b + ks * depth, y3 = depth;
      const x4 = -(ks * depth), y4 = depth;
      const a = (b + ks * depth) * depth;
      let cy = (1 / (6 * a)) * (
        (y1 + y2) * (x1 * y2 - x2 * y1) +
        (y2 + y3) * (x2 * y3 - x3 * y2) +
        (y3 + y4) * (x3 * y4 - x4 * y3) +
        (y4 + y1) * (x4 * y1 - x1 * y4)
      );
      cy = depth - cy;
      return { a, cy };
    };

    const { a: aa, cy: cya } = computeCentroidArea(tir);
    const m1 = cya * aa + (q * q) / (9.81 * aa);

    // Newton-Raphson partiendo de yc1 = 35
    let yc1 = 35;
    let p1 = 2;
    let m2 = 0;
    let iter = 0;
    while (Math.abs(p1) >= 0.00000001 && iter < 500) {
      const a = (b + ks * yc1) * yc1;
      const t = b + 2 * ks * yc1;
      const { cy } = computeCentroidArea(yc1);
      const k = cy / yc1;
      p1 = cy * a + (q * q) / (9.81 * a) - m1;
      const p2 = k * a + k * yc1 * t - (q * q * t) / (9.81 * a * a);
      yc1 = yc1 - p1 / p2;
      m2 = cy * a + (q * q) / (9.81 * a);
      iter++;
    }

    let y1Final: number;
    let y2Final: number;

    if (Math.abs(yc1 - tir) <= 0.00001) {
      // Tirante ingresado es subcrítico: buscar el conjugado menor (supercrítico)
      yc1 = 0.0000025;
      p1 = 1;
      iter = 0;
      while (Math.abs(p1) >= 0.00000001 && iter < 500) {
        const a = (b + ks * yc1) * yc1;
        const t = b + 2 * ks * yc1;
        const { cy } = computeCentroidArea(yc1);
        const k = cy / yc1;
        p1 = cy * a + (q * q) / (9.81 * a) - m1;
        const p2 = k * a + k * yc1 * t - (q * q * t) / (9.81 * a * a);
        yc1 = yc1 - p1 / p2;
        m2 = cy * a + (q * q) / (9.81 * a);
        iter++;
      }
      y1Final = yc1;
      y2Final = tir;
    } else {
      y2Final = yc1;
      y1Final = tir;
    }

    // Tirante crítico (Newton-Raphson)
    let yc = 1;
    let pn1 = (q * q) / 9.81;
    let pn2 = 100;
    iter = 0;
    while (Math.abs(pn1 - pn2) >= 0.00000001 && iter < 500) {
      const an = (b + ks * yc) * yc;
      const asl = b + 2 * ks * yc;
      pn2 = Math.pow(an, 3) / asl;
      const pn3 = 3 * Math.pow(an, 2) - (2 * ks * Math.pow(an, 3)) / Math.pow(asl, 2);
      const ptn = (pn2 - pn1) / pn3;
      yc = yc - ptn;
      iter++;
    }

    // Froude conjugado mayor
    let asl2 = b + 2 * ks * y2Final;
    let an2 = (b + ks * y2Final) * y2Final;
    const f2 = (q / an2) / Math.pow((9.81 * an2) / asl2, 0.5);

    // Froude conjugado menor
    let asl1 = b + 2 * ks * y1Final;
    let an1 = (b + ks * y1Final) * y1Final;
    const f1 = (q / an1) / Math.pow((9.81 * an1) / asl1, 0.5);

    // Energía
    const a1Area = (b + ks * y1Final) * y1Final;
    const a2Area = (b + ks * y2Final) * y2Final;
    const h1 = y1Final + (q * q) / (2 * 9.81 * a1Area * a1Area);
    const h2 = y2Final + (q * q) / (2 * 9.81 * a2Area * a2Area);
    const ener = h1 - h2;
    const df = Math.abs(y2Final - y1Final);
    const efi = 1 - h2 / h1;

    // Longitudes del salto
    let lsaltop = 0, lsaltosilv = 0, lsaltosie = 0;
    if (ks === 0 || b === 0) {
      lsaltosilv = 9.75 * y1Final * Math.pow(f1 - 1, 1.01);
      lsaltop = y2Final * (3.491 + 0.73 * f1 - 0.06 * Math.pow(f1, 2) + 0.001441 * Math.pow(f1, 3));
      lsaltosie = 0;
    } else {
      lsaltosilv = 0;
      lsaltop = 0;
      lsaltosie = 7.1 * (1 + 10 / (b / y1Final)) * (y2Final - y1Final);
    }

    // Tipo de salto
    let tipo = '';
    if (f1 > 1 && f1 <= 1.7) tipo = 'Salto Ondular';
    else if (f1 > 1.7 && f1 <= 2.5) tipo = 'Salto Débil';
    else if (f1 > 2.5 && f1 <= 4.5) tipo = 'Salto Oscilante';
    else if (f1 > 4.5 && f1 <= 9) tipo = 'Salto Estable';
    else tipo = 'Salto Fuerte';

    setResults({
      CMenor: trunc(y1Final).toString(),
      CMayor: trunc(y2Final).toString(),
      FMenor: trunc(f1).toString(),
      FMayor: trunc(f2).toString(),
      MMenor: trunc(m1).toString(),
      MMayor: trunc(m2).toString(),
      RMenor: 'Supercrítico',
      RMayor: 'Subcrítico',
      tirc: trunc(yc).toString(),
      LSaltoP: trunc(lsaltop).toString(),
      LSaltoS: trunc(lsaltosilv).toString(),
      LSaltoSi: trunc(lsaltosie).toString(),
      penergia: trunc(ener).toString(),
      asalto: trunc(df).toString(),
      Esalto: trunc(efi).toString(),
      tsalto: tipo
    });
  };

  const limpiar = () => {
    setGasto('');
    setAncho('');
    setTalud('');
    setTirConjugado('');
    setResults(emptyResults);
    setError('');
  };

  const exportarPNG = async () => {
    if (!exportRef.current) return;
    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `salto-hidraulico-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error('Error al exportar PNG', e);
    }
  };

  const ResultField = ({ label, value }: { label: string; value: string }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        readOnly
        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 font-mono"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-8">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-fuchsia-600 to-purple-600 rounded-t-2xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Salto Hidráulico</h2>
              <p className="text-fuchsia-100 text-sm">Trapecial, Rectangular y Triangular</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div ref={exportRef} className="bg-white">
          <div className="px-6 pt-6 pb-2">
            <p className="text-sm text-gray-600 leading-relaxed">
              Ingrese los datos del canal de sección trapecial, rectangular o triangular.
              El sistema calculará tirantes conjugados, momentos, números de Froude, energía,
              longitud y tipo de salto hidráulico.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Datos de entrada */}
            <div className="bg-gradient-to-br from-fuchsia-50 to-purple-50 rounded-xl p-5 border border-fuchsia-100">
              <h3 className="flex items-center gap-2 text-base font-bold text-fuchsia-900 mb-4">
                <Waves className="w-5 h-5" />
                Datos de entrada
              </h3>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Q = Gasto o Caudal (m³/s)
                  </label>
                  <input
                    type="number"
                    value={gasto}
                    onChange={(e) => setGasto(e.target.value)}
                    min="0"
                    max="10000"
                    step="any"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent transition-all"
                    placeholder="Ej: 5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    B = Ancho de plantilla (m)
                  </label>
                  <input
                    type="number"
                    value={ancho}
                    onChange={(e) => setAncho(e.target.value)}
                    min="0"
                    max="1000"
                    step="any"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent transition-all"
                    placeholder="0 si es triangular"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    K = Talud (Adimensional)
                  </label>
                  <input
                    type="number"
                    value={talud}
                    onChange={(e) => setTalud(e.target.value)}
                    min="0"
                    max="100"
                    step="any"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent transition-all"
                    placeholder="0 si es rectangular"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tirante Conjugado (m)
                  </label>
                  <input
                    type="number"
                    value={tirConjugado}
                    onChange={(e) => setTirConjugado(e.target.value)}
                    min="0"
                    max="100"
                    step="any"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent transition-all"
                    placeholder="Tirante conocido"
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={calcular}
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white font-semibold py-2.5 px-4 rounded-lg hover:shadow-lg transition-all"
                  >
                    <Calculator className="w-4 h-4" />
                    Calcular
                  </button>
                  <button
                    onClick={limpiar}
                    className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 font-semibold py-2.5 px-4 rounded-lg hover:bg-gray-200 transition-all"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Limpiar
                  </button>
                </div>
              </div>
            </div>

            {/* Resultados */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-5 border border-blue-100">
              <h3 className="flex items-center gap-2 text-base font-bold text-blue-900 mb-4">
                <Calculator className="w-5 h-5" />
                Resultados
              </h3>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <ResultField label="Tirante Conj. Menor (m)" value={results.CMenor} />
                  <ResultField label="Tirante Conj. Mayor (m)" value={results.CMayor} />
                  <ResultField label="Momento Conj. Menor" value={results.MMenor} />
                  <ResultField label="Momento Conj. Mayor" value={results.MMayor} />
                  <ResultField label="Froude Conj. Menor" value={results.FMenor} />
                  <ResultField label="Froude Conj. Mayor" value={results.FMayor} />
                  <ResultField label="Régimen Conj. Menor" value={results.RMenor} />
                  <ResultField label="Régimen Conj. Mayor" value={results.RMayor} />
                  <ResultField label="Tirante Crítico (m)" value={results.tirc} />
                  <ResultField label="L. Salto Pavlovski (m)*" value={results.LSaltoP} />
                  <ResultField label="L. Salto Silvester (m)*" value={results.LSaltoS} />
                  <ResultField label="L. Salto Sieñchin (m)*" value={results.LSaltoSi} />
                  <ResultField label="Pérdida Energía (m)" value={results.penergia} />
                  <ResultField label="Altura del Salto (m)" value={results.asalto} />
                  <ResultField label="Eficiencia (Adim)" value={results.Esalto} />
                  <ResultField label="Tipo de Salto" value={results.tsalto} />
                </div>

                <p className="text-xs text-gray-500 italic pt-2 border-t border-blue-200">
                  * Si el valor es 0 significa que la fórmula está fuera de rango.
                </p>
              </div>
            </div>
          </div>

          {/* Footer marca para PNG */}
          <div className="px-6 pb-4 pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
            <span>Generado por openHH · HHTools</span>
            <span>{new Date().toLocaleString()}</span>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex justify-end gap-2 px-6 pb-6">
          <button
            onClick={exportarPNG}
            className="flex items-center gap-2 bg-gray-900 text-white font-semibold py-2.5 px-5 rounded-lg hover:bg-gray-700 transition-all"
          >
            <Download className="w-4 h-4" />
            Exportar PNG
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaltoHidraulicoCalculator;
